import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/shared/Logo";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Crown, Building2, ShieldCheck, BarChart3, MessageSquareQuote,
  BookOpen, Gift, HelpCircle, FileText, Scale, Ticket, LogOut, Home, Menu, Tag,
} from "lucide-react";

const groups = [
  { label: "Overview", items: [["/admin", "Dashboard", LayoutDashboard]] },
  { label: "Members", items: [
    ["/admin/users", "Users", Users],
    ["/admin/vip", "VIP", Crown],
    ["/admin/referrals", "Referrals", Gift],
  ]},
  { label: "Brokers", items: [
    ["/admin/brokers", "Brokers", Building2],
    ["/admin/verifications", "Verifications", ShieldCheck],
  ]},
  { label: "Content", items: [
    ["/admin/results", "Results", BarChart3],
    ["/admin/testimonials", "Testimonials", MessageSquareQuote],
    ["/admin/education", "Education", BookOpen],
    ["/admin/offers", "Offers", Tag],
    ["/admin/faq", "FAQ", HelpCircle],
  ]},
  { label: "Website", items: [
    ["/admin/content", "Site Content", FileText],
    ["/admin/legal", "Legal", Scale],
  ]},
  { label: "Support", items: [["/admin/tickets", "Tickets", Ticket]] },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-brand-navy flex">
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-brand-navy2 border-r border-white/10 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <Logo size="h-7" to="/admin" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          {groups.map((g) => (
            <div key={g.label}>
              <p className="px-3 mb-1.5 text-[10px] uppercase tracking-widest text-slate-600 font-semibold">{g.label}</p>
              <div className="space-y-0.5">
                {g.items.map(([path, label, Icon]) => (
                  <NavLink key={path} to={path} end={path === "/admin"} onClick={() => setOpen(false)}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-brand-cyan/10 text-brand-cyan font-medium" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/5"><Home className="h-4 w-4" /> Public Site</button>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 min-w-0">
        <header className="h-16 border-b border-white/10 glass flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" data-testid="admin-menu-toggle"><Menu className="h-5 w-5" /></button>
          <p className="font-display font-semibold text-white">Admin Console</p>
          <div className="flex items-center gap-3 text-sm text-slate-400">
            <ThemeSwitcher />
            <span className="hidden sm:inline">{user?.email}</span>
            <div className="h-8 w-8 rounded-full gradient-brand flex items-center justify-center text-brand-navy font-bold text-sm">{(user?.name||"A").charAt(0)}</div>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1400px] mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
